import axios from "axios";
import {
  MSAL_CONFIGURED,
  acquireApiAccessToken,
  getActiveAccount,
  initMsal,
  requireInteractiveAuth,
  isInIframe,
  BrowserAuthError,
} from "../utils/msal";

const TARGET_ORG_HEADER = "X-Target-Org-Id";
const TARGET_ORG_STORAGE_KEY = "targetOrgId";
const DATA_ORG_HEADER = "X-Data-Org-Id";
const DATA_ORG_STORAGE_KEY = "dataOrgId";

// Identity endpoints that resolve which orgs the user may scope to, so they must never carry an unvalidated scope themselves.
const ORG_SCOPE_EXEMPT_PATHS = ["/auth/me", "/users/me/organisations"];

const isOrgScopeExempt = (url = "") =>
  ORG_SCOPE_EXEMPT_PATHS.some((path) => url.split("?")[0].endsWith(path));

// Endpoints whose 401 must never trigger a refresh attempt, otherwise the interceptor re-enters itself.
const AUTH_ENDPOINTS = [
  "/auth/login",
  "/auth/token/verify",
  "/auth/token/refresh",
];

const isAuthEndpoint = (url = "") =>
  AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));

const instance = axios.create({
  baseURL: "/api/v3",
});

const clearSession = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("assets-standard");
  localStorage.removeItem(TARGET_ORG_STORAGE_KEY);
  localStorage.removeItem(DATA_ORG_STORAGE_KEY);
};

let sessionExpiredHandler = null;

export const setSessionExpiredHandler = (handler) => {
  sessionExpiredHandler = handler;
  return () => {
    if (sessionExpiredHandler === handler) sessionExpiredHandler = null;
  };
};

const endSession = () => {
  clearSession();

  if (sessionExpiredHandler) {
    sessionExpiredHandler();
    return;
  }

  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
};

instance.interceptors.request.use(async (config) => {
  let attached = false;

  if (MSAL_CONFIGURED) {
    await initMsal();
    if (getActiveAccount()) {
      try {
        const token = await acquireApiAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          attached = true;
        }
      } catch (error) {
        // block_iframe_reload: this request is running inside MSAL's silent iframe; do not start a redirect.
        if (
          error instanceof BrowserAuthError &&
          error.errorCode === "block_iframe_reload"
        ) {
          throw new axios.Cancel("MSAL silent iframe");
        }
        // Any other BrowserAuthError means the cached session can't issue a token, so re-auth rather than send unauthenticated.
        if (error instanceof BrowserAuthError && !isInIframe()) {
          requireInteractiveAuth();
          throw new axios.Cancel("MSAL interactive re-auth required");
        }
        console.warn("MSAL silent token acquisition failed:", error);
        throw error;
      }
    }
  }

  if (!attached) {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  const targetOrgId = localStorage.getItem(TARGET_ORG_STORAGE_KEY);
  if (targetOrgId && !isOrgScopeExempt(config.url)) {
    config.headers[TARGET_ORG_HEADER] = targetOrgId;
  }
  const dataOrgId = localStorage.getItem(DATA_ORG_STORAGE_KEY);
  if (dataOrgId && dataOrgId !== targetOrgId && !isOrgScopeExempt(config.url)) {
    config.headers[DATA_ORG_HEADER] = dataOrgId;
  }
  return config;
});

const REFRESH_PATH = "auth/token/refresh";

let refreshPromise = null;

/** Single-flight refresh: a burst of parallel 401s triggers exactly one refresh call. */
const refreshAccessToken = () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = axios
    .post(`/api/v3/${REFRESH_PATH}`, {
      refresh: localStorage.getItem("refreshToken"),
    })
    .then(({ data }) => {
      localStorage.setItem("accessToken", data.access);
      // Honour a rotated refresh token should the backend ever enable rotation.
      if (data.refresh) localStorage.setItem("refreshToken", data.refresh);
      instance.defaults.headers.common.Authorization = `Bearer ${data.access}`;
      return data.access;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const bearerOf = (config) => {
  const header =
    config?.headers?.Authorization || config?.headers?.authorization;
  return typeof header === "string" ? header.replace(/^Bearer /, "") : null;
};

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint(originalRequest.url)
    ) {
      originalRequest._retry = true;

      // MSAL session: silent refresh is MSAL's job, so a 401 here means the session is dead.
      if (MSAL_CONFIGURED && getActiveAccount()) {
        return Promise.reject(error);
      }

      if (!localStorage.getItem("refreshToken")) {
        endSession();
        return Promise.reject(error);
      }

      // A newer stored token means another request already refreshed: just retry.
      const currentToken = localStorage.getItem("accessToken");
      if (currentToken && currentToken !== bearerOf(originalRequest)) {
        originalRequest.headers.Authorization = `Bearer ${currentToken}`;
        return instance(originalRequest);
      }

      try {
        const newAccess = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return instance(originalRequest);
      } catch (refreshError) {
        endSession();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export { clearSession };
export default instance;
export { TARGET_ORG_STORAGE_KEY, DATA_ORG_STORAGE_KEY };
